import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Globe, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  Plus, 
  Trash2, 
  RefreshCw, 
  Copy, 
  Star,
  ExternalLink,
  Shield,
  Check,
  X,
  Send
} from 'lucide-react';
import api from '../utils/api';

/**
 * WhiteLabelEmailDomains Component
 * 
 * Allows tenants with White Label subscription to:
 * - Add and verify their own sending domains
 * - View DNS records to configure
 * - Set a default sending domain
 * - Customize sender name and email
 * - Send test emails to preview branding
 */
const WhiteLabelEmailDomains = ({ user, onNotification }) => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Add form state
  const [newDomain, setNewDomain] = useState({
    domain: '',
    from_name: '',
    from_email: '',
    reply_to_email: '',
    subdomain: 'em'
  });

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      setError(null);
      setAccessDenied(false);
      
      const token = localStorage.getItem('access_token');
      const response = await api.get('/settings/email-domains', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setDomains(response.data.domains || []);
      }
    } catch (err) {
      console.error('Failed to fetch email domains:', err);
      
      // Check if it's a white-label access error
      if (err.response?.status === 403 && err.response?.data?.detail?.error === 'white_label_required') {
        setAccessDenied(true);
      } else {
        setError('Failed to load email domains');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    
    if (!newDomain.domain || !newDomain.from_name) {
      onNotification?.('error', 'Domain and From Name are required');
      return;
    }
    
    try {
      setAdding(true);
      const token = localStorage.getItem('access_token');
      
      const response = await api.post('/settings/email-domains', newDomain, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        onNotification?.('success', 'Domain added! Configure the DNS records shown below.');
        setShowAddForm(false);
        setNewDomain({
          domain: '',
          from_name: '',
          from_email: '',
          reply_to_email: '',
          subdomain: 'em'
        });
        // Expand the new domain to show DNS records
        setExpandedDomain(response.data.id);
        fetchDomains();
      }
    } catch (err) {
      console.error('Failed to add domain:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to add domain';
      onNotification?.('error', typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setAdding(false);
    }
  };

  const handleVerifyDomain = async (domainId) => {
    try {
      setVerifying(domainId);
      const token = localStorage.getItem('access_token');
      
      const response = await api.post(`/settings/email-domains/${domainId}/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        onNotification?.('success', response.data.message || 'Domain verified successfully!');
      } else {
        onNotification?.('error', response.data.message || 'DNS records not yet configured');
      }
      
      fetchDomains();
    } catch (err) {
      console.error('Failed to verify domain:', err);
      onNotification?.('error', err.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(null);
    }
  };

  const handleSetDefault = async (domainId) => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await api.post(`/settings/email-domains/${domainId}/set-default`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        onNotification?.('success', 'Default sender domain updated');
        fetchDomains();
      }
    } catch (err) {
      console.error('Failed to set default domain:', err);
      onNotification?.('error', err.response?.data?.detail || 'Failed to set default');
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Are you sure you want to delete this domain? This cannot be undone.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      
      await api.delete(`/settings/email-domains/${domainId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      onNotification?.('success', 'Domain deleted');
      fetchDomains();
    } catch (err) {
      console.error('Failed to delete domain:', err);
      onNotification?.('error', err.response?.data?.detail || 'Failed to delete domain');
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setSendingTest(true);
      const token = localStorage.getItem('access_token');
      
      const response = await api.post('/settings/email-domains/send-test', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        onNotification?.('success', response.data.message || 'Test email sent! Check your inbox.');
      }
    } catch (err) {
      console.error('Failed to send test email:', err);
      onNotification?.('error', err.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    onNotification?.('success', 'Copied to clipboard');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'verifying':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'verifying':
        return 'Verifying...';
      case 'failed':
        return 'Verification Failed';
      default:
        return 'Pending DNS Setup';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'verifying':
        return 'bg-blue-100 text-blue-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  // Access denied view for non-white-label users
  if (accessDenied) {
    return (
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              White Label Email Domains
            </h3>
            <p className="text-gray-600 mb-4">
              Send emails from your own domain (e.g., notifications@yourcompany.com) instead of our default address. 
              This feature is available with the White Label add-on.
            </p>
            <div className="flex items-center space-x-3">
              <span className="inline-flex items-center px-3 py-1.5 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                <Star className="w-4 h-4 mr-1" />
                White Label Add-on Required
              </span>
              <a 
                href="/settings?group=billing&section=subscription" 
                className="text-purple-600 hover:text-purple-700 text-sm font-medium flex items-center"
              >
                Upgrade Now
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        <div className="h-24 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <Globe className="w-5 h-5 text-purple-600" />
            <span>Custom Email Domains</span>
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Send emails from your own verified domain
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleSendTestEmail}
            disabled={sendingTest}
            className="flex items-center space-x-2 px-4 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 disabled:opacity-50 transition-colors"
            title="Send a test email to yourself to preview your branding"
          >
            {sendingTest ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Send Test Email</span>
              </>
            )}
          </button>
          
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Domain</span>
          </button>
        </div>
      </div>

      {/* Add Domain Form */}
      {showAddForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h4 className="font-medium text-gray-900 mb-4">Add New Sending Domain</h4>
          
          <form onSubmit={handleAddDomain} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domain <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newDomain.domain}
                  onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                  placeholder="yourcompany.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Enter your domain without https:// or www</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newDomain.from_name}
                  onChange={(e) => setNewDomain({ ...newDomain, from_name: e.target.value })}
                  placeholder="Your Company Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">This appears as the sender name in email clients</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Email
                </label>
                <input
                  type="email"
                  value={newDomain.from_email}
                  onChange={(e) => setNewDomain({ ...newDomain, from_email: e.target.value })}
                  placeholder="notifications@yourcompany.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reply-To Email
                </label>
                <input
                  type="email"
                  value={newDomain.reply_to_email}
                  onChange={(e) => setNewDomain({ ...newDomain, reply_to_email: e.target.value })}
                  placeholder="support@yourcompany.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">Where replies should go</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
              <button
                type="submit"
                disabled={adding}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {adding ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Adding...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    <span>Add Domain</span>
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Domains List */}
      {domains.length === 0 && !showAddForm ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No Custom Domains Yet</h4>
          <p className="text-gray-600 mb-4">
            Add your own domain to send emails from your company address instead of our default.
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Your First Domain</span>
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {domains.map((domain) => (
            <div 
              key={domain.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm"
            >
              {/* Domain Header */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedDomain(expandedDomain === domain.id ? null : domain.id)}
              >
                <div className="flex items-center space-x-4">
                  {getStatusIcon(domain.verification_status)}
                  
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{domain.domain}</span>
                      {domain.is_default && (
                        <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                          <Star className="w-3 h-3 mr-1" />
                          Default
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {domain.from_name} &lt;{domain.from_email}&gt;
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusColor(domain.verification_status)}`}>
                    {getStatusText(domain.verification_status)}
                  </span>
                  
                  <svg 
                    className={`w-5 h-5 text-gray-400 transition-transform ${expandedDomain === domain.id ? 'rotate-180' : ''}`}
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              
              {/* Expanded Details */}
              {expandedDomain === domain.id && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  {/* DNS Records */}
                  {domain.verification_status !== 'verified' && domain.dns_records?.length > 0 && (
                    <div className="mb-6">
                      <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                        <Shield className="w-4 h-4 mr-2 text-blue-600" />
                        DNS Records to Configure
                      </h5>
                      <p className="text-sm text-gray-600 mb-3">
                        Add these DNS records to your domain host, then click Verify Domain.
                      </p>
                      
                      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Type</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Host/Name</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Value/Data</th>
                              <th className="px-4 py-2 text-left text-gray-600 font-medium">Status</th>
                              <th className="px-4 py-2"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {domain.dns_records.map((record, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 font-mono text-xs">{record.type}</td>
                                <td className="px-4 py-3 font-mono text-xs break-all max-w-xs">{record.host}</td>
                                <td className="px-4 py-3 font-mono text-xs break-all max-w-xs">{record.data}</td>
                                <td className="px-4 py-3">
                                  {record.valid ? (
                                    <Check className="w-4 h-4 text-green-500" />
                                  ) : (
                                    <X className="w-4 h-4 text-gray-400" />
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(record.data);
                                    }}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                    title="Copy value"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Note:</strong> DNS changes can take up to 48 hours to propagate, 
                          though most providers update within minutes. You can click Verify Domain 
                          multiple times to check the status.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Verification Error */}
                  {domain.verification_error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-800">
                        <strong>Last verification error:</strong> {domain.verification_error}
                      </p>
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="flex items-center space-x-3">
                    {domain.verification_status !== 'verified' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVerifyDomain(domain.id);
                        }}
                        disabled={verifying === domain.id}
                        className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                      >
                        {verifying === domain.id ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            <span>Verifying...</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            <span>Verify Domain</span>
                          </>
                        )}
                      </button>
                    )}
                    
                    {domain.verification_status === 'verified' && !domain.is_default && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSetDefault(domain.id);
                        }}
                        className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        <Star className="w-4 h-4" />
                        <span>Set as Default</span>
                      </button>
                    )}
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDomain(domain.id);
                      }}
                      className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                  
                  {/* Verified Success Message */}
                  {domain.verification_status === 'verified' && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        <span>
                          <strong>Domain verified!</strong> Emails will now be sent from{' '}
                          <span className="font-mono">{domain.from_email}</span>
                          {domain.is_default ? ' (default sender)' : ''}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {/* Help Text */}
      <div className="text-sm text-gray-500 space-y-2">
        <p>
          <strong>How it works:</strong> After adding your domain, you will need to add DNS records 
          to your domain provider (like GoDaddy, Cloudflare, or Namecheap). Once configured, 
          click Verify Domain to complete the setup.
        </p>
        <p>
          <strong>Benefits:</strong> Emails from your own domain improve deliverability and brand 
          recognition. Your clients will see emails from your company instead of our platform.
        </p>
      </div>
    </div>
  );
};

export default WhiteLabelEmailDomains;
